import { useMemo } from "react";
import { useApi, apiResolver } from "@/api/useApi";
import { QuackChatSystemMessageType } from "./Quackchat.model";
import { MessageFilter } from "@/features/automation-configuration/quackchat-channel/system-message/SystemMessage.model";

type GetAllSystemMessageRequest = {
  tenant: string;
  simulatorId?: string;
};

type GetAllSystemMessagesResponse = {
  messages: (GetSystemMessageResponse & { type: QuackChatSystemMessageType })[];
};

type GetSystemMessageRequest = {
  type: QuackChatSystemMessageType;
  tenant: string;
  simulatorId?: string;
};

export type GetSystemMessageResponse = {
  message: string;
  enabled: boolean;
};

type GetSystemMessageWithConditionsResponse = {
  message: string;
  id: string;
  conditions: MessageFilter[];
};

type OverrideSystemMessageRequest = {
  tenant: string;
  type: QuackChatSystemMessageType;
  message: string;
  simulatorId?: string;
  conditions?: MessageFilter[];
  id?: string;
};

type ToggleSystemMessageRequest = {
  tenant: string;
  type: QuackChatSystemMessageType;
  enabled: boolean;
  simulatorId?: string;
};

type ToggleSystemMessageResponse = {
  enabled: boolean;
};

export function useQuackchatApi() {
  const { api } = useApi();

  return useMemo(
    () => ({
      getAllSystemMessages: ({
        tenant,
        simulatorId,
      }: GetAllSystemMessageRequest) =>
        apiResolver<GetAllSystemMessagesResponse>(
          api.post(`/sessions-api/quackchat/system-messages`, {
            tenant,
            simulatorId,
          })
        ),
      getAllSystemMessageConditions: ({
        type,
        tenant,
        simulatorId,
      }: GetSystemMessageRequest) =>
        apiResolver<GetSystemMessageWithConditionsResponse[]>(
          api.post(`/sessions-api/quackchat/system-messages/with-conditions`, {
            tenant,
            simulatorId,
            type,
          })
        ),
      getSystemMessage: ({
        type,
        tenant,
        simulatorId,
      }: GetSystemMessageRequest) =>
        apiResolver<GetSystemMessageResponse>(
          api.post(`/sessions-api/quackchat/system-messages/${type}`, {
            tenant,
            simulatorId,
          })
        ),
      overrideSystemMessage: ({
        type,
        tenant,
        message,
        simulatorId,
        conditions,
        id
      }: OverrideSystemMessageRequest) =>
        apiResolver<GetSystemMessageResponse>(
          api.put(
            `/sessions-api/quackchat/system-messages/${type}/override${
              simulatorId ? `?simulatorId=${simulatorId}` : ""
            }`,
            {
              tenant,
              message,
              simulatorId,
              conditions,
              id,
            }
          )
        ),
      toggleSystemMessage: ({
        type,
        tenant,
        enabled,
        simulatorId,
      }: ToggleSystemMessageRequest) =>
        apiResolver<ToggleSystemMessageResponse>(
          api.put(
            `/sessions-api/quackchat/system-messages/${type}/toggle${
              simulatorId ? `?simulatorId=${simulatorId}` : ""
            }`,
            {
              tenant,
              enabled,
              simulatorId,
            }
          )
        ),
    }),
    [api]
  );
}
